import React, {useEffect, useState} from "react";
import Pagination from "../../components/form/Pagination";
import TextInput from "../../components/form/TextInput";
import {debounce} from "../../utils/debounce";
import {SET_IS_API_CALLED, SET_IS_API_CALLED_FINISHED,} from "../../store/features/loadingSlice";
import {getMeterDailyUsageReportApi, getMeterDailyUsageReportExcelDownload} from "../../Services/reports";
import dayjs from "dayjs";
import {useNavigate, useParams, useSearchParams,} from "react-router-dom/dist";
import {useDispatch, useSelector} from "react-redux";
import DateRange from "../../components/form/DateRange";
import Highlighter from "react-highlight-words";
import DescendingIcon from "../../components/Icons/descending";
import AscendingIcon from "../../components/Icons/ascending";
import {copyToClipboard} from "../../utils/clipBoard";
import {Modal} from "antd";
import {TablePageSize} from "../../constant";
import {AlarmErrorDetailTablePermision} from "./constant";
import {updateAlarmAcknowledgeApi} from "../../Services/alarm";
import StrengthBars from "../../components/indicators/StrengthBars";
import { useClientTableSort } from "../../hooks/useClientTableSort";
import SortableTh from "../../components/table/SortableTh";
import {ExcelFileGeneratorUtil} from "../../utils/ExcelUtils/ExcelFileGeneratorUtil";

function parseQueryDateToDayjs(param) {
    if (param == null || String(param).trim() === "") return null;
    const trimmed = String(param).trim();
    const asNum = Number(trimmed);
    if (!Number.isNaN(asNum) && trimmed === String(asNum)) {
        const fromMillis = dayjs(asNum);
        return fromMillis.isValid() ? fromMillis : null;
    }
    const parsed = dayjs(trimmed);
    return parsed.isValid() ? parsed : null;
}

function DailyUsage({showSearch = true, searchText}) {
    const {ROLE} = useSelector((state) => state.Auth);
    const headers = AlarmErrorDetailTablePermision(ROLE);

    let {id} = useParams();
    const [filterParams, setFilterParams] = useState({
        pageSize: TablePageSize,
        serialNumber: id,
    });
    const dispatch = useDispatch();
    const [meterDailyUsageData, setMeterDailyUsageData] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [changeStatus, setChangeStatus] = useState([]);
    const [items, setItems] = useState([]);
    const [sort, setSort] = useState([]);
    const { headerSort, sortByColumn, resetSort } = useClientTableSort(setItems);

    const [searchParams] = useSearchParams();
    const startQ = searchParams.get("start") ?? "";
    const endQ = searchParams.get("end") ?? "";

    const defaultStart = () => dayjs().subtract(1, "month");
    const defaultEnd = () => dayjs();

    const [selectedDate, setSelectedDate] = useState(() => {
        const startD = parseQueryDateToDayjs(startQ) ?? defaultStart();
        const endD = parseQueryDateToDayjs(endQ) ?? defaultEnd();
        return [startD, endD];
    });

    useEffect(() => {
        const startD = parseQueryDateToDayjs(startQ);
        const endD = parseQueryDateToDayjs(endQ);
        if (startD && endD) {
            setSelectedDate([startD, endD]);
        }
    }, [startQ, endQ]);

    useEffect(() => {
        dispatch(SET_IS_API_CALLED("Fetching Data"));
        getMeterDailyUsageReportApi({
            serialNumber: filterParams?.serialNumber,
            pageSize: 10,
            pageIndex: meterDailyUsageData?.pageIndex,
            startDate: dayjs(selectedDate[0]).format('YYYY-MM-DDTHH:mm:ss'),
            endDate: dayjs(selectedDate[1]).format('YYYY-MM-DDTHH:mm:ss'),
            ...filterParams,
        }).then((res) => {
            setMeterDailyUsageData(res?.data);
            resetSort();
            setItems(res?.data?.items);
            dispatch(SET_IS_API_CALLED_FINISHED());
        });
    }, [filterParams, selectedDate]);

    const debouncedOnFilter = debounce((val) => {
        setFilterParams({
            ...filterParams,
            pageSize: val,
            pageIndex: 1,
        });
    }, 500);

    const exportDataToExcelFile = async () => {
        dispatch(SET_IS_API_CALLED("Exporting Excel"));
        try {
            const res = await getMeterDailyUsageReportExcelDownload({
                serialNumber: filterParams?.serialNumber,
                startDate: dayjs(selectedDate[0]).format("YYYY-MM-DDTHH:mm:ss"),
                endDate: dayjs(selectedDate[1]).format("YYYY-MM-DDTHH:mm:ss"),
            });

            const data = res?.data?.map((item) => ({
                ...item,
                readingDate:
                    item.readingDate && dayjs(item.readingDate).isValid()
                        ? dayjs(item.readingDate).format("DD/MM/YYYY h:mm:ss a")
                        : "N/A",
            }));

            ExcelFileGeneratorUtil({
                filename: `daily-usage-${id}-${dayjs(selectedDate[0]).format("DDMMYYYY")}-${dayjs(selectedDate[1]).format("DDMMYYYY")}.xlsx`,
                formattedDataForExcelFile: data,
            });
        } finally {
            dispatch(SET_IS_API_CALLED_FINISHED());
        }
    };

    const handlePageClick = (number) => {
        dispatch(SET_IS_API_CALLED("Fetching Data"));
        getMeterDailyUsageReportApi({
            pageIndex: number,
            serialNumber: filterParams?.serialNumber,
            startDate: dayjs(selectedDate[0]).format('YYYY-MM-DDTHH:mm:ss'),
            endDate: dayjs(selectedDate[1]).format('YYYY-MM-DDTHH:mm:ss'),
            ...filterParams,
        }).then((res) => {
            setMeterDailyUsageData(res?.data);
            resetSort();
            setItems(res?.data?.items);
        }).finally(() => {
            dispatch(SET_IS_API_CALLED_FINISHED());
        });
    };
    const selectedIdsChange = (selectedId) => {
        if (selectedIds?.includes(selectedId)) {
            setSelectedIds(selectedIds?.filter((id) => id !== selectedId));
        } else {
            setSelectedIds([...selectedIds, selectedId]);
        }
    };
    const changeStatusIds = (selectedItem) => {
        const {status, id} = selectedItem;
        const isSelected = changeStatus.some((item) => item.id === id);
        if (isSelected) {
            setChangeStatus(changeStatus.filter((item) => item.id !== id));
        } else {
            setChangeStatus([...changeStatus, {status, id}]);
        }
    };

    function customSort(a, b) {
        if (selectedIds?.includes(a?.accountId)) {
            return -1;
        }
        if (selectedIds?.includes(b?.accountId)) {
            return 1;
        }
        return a.accountId - b.accountId;
    }

    function customSortClick() {
        const data = items?.slice().sort(customSort);
        setItems(data);
    }

    const [isModalOpen, setIsModalOpen] = useState(false);
    const showModal = () => {
        setIsModalOpen(true);
    };
    const handleOk = () => {
        const data = changeStatus.map((val) => {
            return {
                AlarmId: val.id,
                IsAcknowledged: val.status === "Yes",
            };
        });
        updateAlarmAcknowledgeApi(data).then((res) => {
            if (res?.status === 200) {
                setIsModalOpen(false);
                setChangeStatus([]);
            }
        });
    };
    const handleCancel = () => {
        setIsModalOpen(false);
    };
    const navigate = useNavigate();

    return (
        <div className=" bg-white px-5 pt-2">
            {showSearch && (
                <div className="flex justify-between">
                    <div className="flex gap-4">
                        <div
                            className=" my-4 cursor-pointer"
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            <div
                                className=" flex justify-center items-center gap-5 bg-dark-blue-200 hover:bg-blue-600 text-white w-[170px] h-[45px] font-bold text-lg ">
                                <img
                                    src="/assets/Icon%20ionic-ios-arrow-back.svg"
                                    width={6}
                                    height={11}
                                    alt="Plus"
                                />
                                <span>Back</span>
                            </div>
                        </div>
                        <form className="flex items-center gap-3 justify-start ">
                            <TextInput
                                value={filterParams?.serialNumber}
                                onChange={debouncedOnFilter}
                                placeholder={"XXXXXXXXXXXX"}
                            />
                            <button type="button" onClick={() => {
                            }}>
                                <img src="/assets/search-alt-1-svgrepo-com.svg" alt="Search"/>
                            </button>
                        </form>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            className="h-[40px] w-[40px] flex items-center justify-center"
                            onClick={() => exportDataToExcelFile()}
                            aria-label="Export Excel"
                        >
                            <img
                                src={"/assets/icon-excel.svg"}
                                width={36}
                                height={36}
                                alt="Export Excel"
                            />
                        </button>
                        <div className="w-[280px]">
                            <DateRange
                                value={selectedDate}
                                onChange={(dates) => {
                                    if (Array.isArray(dates) && dates[0] && dates[1]) {
                                        setSelectedDate([dayjs(dates[0]), dayjs(dates[1])]);
                                    }
                                }}
                            />
                        </div>
                        <div
                            className={`${
                                changeStatus.length > 0
                                    ? "bg-dark-blue-200"
                                    : "bg-light-grey-200 "
                            } h-[40px] w-[190px] text-lg font-normal flex justify-center items-center cursor-pointer rounded text-white`}
                            onClick={() => {
                                if (changeStatus.length > 0) showModal();
                            }}
                        >
                            <div>Change</div>
                        </div>
                        <Modal
                            title="Change Status"
                            open={isModalOpen}
                            onOk={handleOk}
                            onCancel={handleCancel}
                        >
                            <p>Are you sure you want to continue?</p>
                        </Modal>
                    </div>
                </div>
            )}
            <div
                className={`relative overflow-x-auto mt-4 border shadow-md overflow-y-auto ${
                    (meterDailyUsageData?.items?.length === 0 ||
                        meterDailyUsageData?.items == null) &&
                    "min-h-[300px]"
                }`}
                style={{maxHeight: "calc(100vh - 340px)"}}
            >
                <table className="w-full relative">
                    <thead
                        className="border-b text-dark-blue-200 bg-white sticky top-0"
                        style={{zIndex: 1}}
                    >
                    <tr className="text-center">
                        {headers?.map((header, index) => {
                            return (
                                <>
                                    {header.key === "actions" ? (
                                        <th>
                                            <div className="flex pl-4 items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    className=""
                                                    onClick={() => {
                                                        if (selectedIds?.length === items?.length) {
                                                            setSelectedIds([]);
                                                        } else setSelectedIds(items?.map((v) => v?.id));
                                                    }}
                                                    checked={selectedIds?.length === items?.length}
                                                />
                                                <div className=" flex items-center w-[31.8px]">
                                                    <div
                                                        className=" cursor-pointer"
                                                        onClick={() => {
                                                            customSortClick();
                                                            setSort(sort === 0 ? 1 : 0);
                                                        }}
                                                    >
                                                        <DescendingIcon isActive={sort === 1}/>
                                                    </div>
                                                    <div
                                                        className=" cursor-pointer"
                                                        onClick={() => {
                                                            customSortClick();
                                                            setSort(sort === 0 ? 1 : 0);
                                                        }}
                                                    >
                                                        <AscendingIcon isActive={sort === 0}/>
                                                    </div>
                                                </div>
                                            </div>
                                        </th>
                                    ) : (
                                        <SortableTh
                                            key={index}
                                            label={header.th}
                                            columnKey={header.key}
                                            headerSort={headerSort}
                                            className="pl-4 py-2"
                                            innerLabelClassName="text-xs font-medium leading-normal"
                                            onSortDescending={(key) => sortByColumn(key, true)}
                                            onSortAscending={(key) => sortByColumn(key, false)}
                                        />
                                    )}
                                </>
                            );
                        })}
                    </tr>
                    </thead>
                    <tbody className=" relative">
                    {items?.length > 0 &&
                        items?.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b ${
                                    index % 2 === 1 ? "bg-white" : "bg-light-grey-100"
                                } text-center   text-light-grey-400`}
                            >
                                {headers?.map((header, headerIndex) => (
                                    <>
                                        {header.key === "actions" && (
                                            <td className="px-4 pr-8 py-3 flex items-center gap-4">
                                                <input
                                                    type="checkbox"
                                                    className=""
                                                    onClick={() => {
                                                        selectedIdsChange(item?.id);
                                                    }}
                                                    checked={selectedIds?.includes(item?.id)}
                                                />
                                                <img
                                                    className=" cursor-pointer"
                                                    src={"/assets/icon-copy.svg"}
                                                    width={16}
                                                    alt="Copy"
                                                    onClick={() => {
                                                        copyToClipboard(item);
                                                    }}
                                                />
                                            </td>
                                        )}
                                        {header.key === "acknowledged" && (
                                            <td
                                                key={headerIndex}
                                                className={`flex justify-center font-normal text-dark-blue px-2 py-3 text-[10px] leading-normal`}
                                            >
                                                {item["item"] === "Alarm" && (
                                                    <select
                                                        className="block w-[64px] h-[30px] text-center text-[10px] leading-normal font-normal text-black border border-light-grey-500 rounded-lg bg-white focus:border-blue-500"
                                                        onChange={(e) => {
                                                            changeStatusIds({
                                                                status: e.target.value,
                                                                id: item?.id,
                                                            });
                                                        }}
                                                    >
                                                        <option selected={item[header.key] === "No"}>
                                                            No
                                                        </option>
                                                        <option selected={item[header.key] === "Yes"}>
                                                            Yes
                                                        </option>
                                                    </select>
                                                )}
                                            </td>
                                        )}
                                        {header.key === "loggerSignalStrength" && (
                                            <td
                                                key={headerIndex}
                                                className="pl-4 py-3 text-[10px] font-normal leading-normal text-medium-grey"
                                            >
                                                <div className="flex justify-center">
                                                    <StrengthBars value={item?.loggerSignalStrength} />
                                                </div>
                                            </td>
                                        )}
                                        {!["acknowledged", "actions", "loggerSignalStrength"]?.includes(
                                            header.key
                                        ) && (
                                            <td
                                                key={headerIndex}
                                                className={`pl-4 font-normal text-medium-grey py-3 text-[10px] leading-normal `}
                                            >
                                                {header.key === "readingDate" ? <div
                                                    className="flex justify-center"
                                                    style={{
                                                        width: item[header.key]?.length * 6 + "px",
                                                    }}
                                                >
                                                    <Highlighter
                                                        highlightClassName="YourHighlightClass"
                                                        searchWords={[searchText]}
                                                        autoEscape={true}
                                                        textToHighlight={
                                                            item.readingDate &&
                                                            dayjs(item.readingDate).isValid()
                                                                ? dayjs(item.readingDate).format(
                                                                      "DD/MM/YYYY h:mm:ss a"
                                                                  )
                                                                : "N/A"
                                                        }
                                                    />
                                                </div>: <div
                                                    className="flex justify-center"
                                                    style={{
                                                        width: item[header.key]?.length * 6 + "px",
                                                    }}
                                                >
                                                    <Highlighter
                                                        highlightClassName="YourHighlightClass"
                                                        searchWords={[searchText]}
                                                        autoEscape={true}
                                                        textToHighlight={
                                                            `${
                                                                !item[header.key] ? "" : item[header.key]
                                                            }` || "N/A"
                                                        }
                                                    />
                                                </div>}
                                            </td>
                                        )}
                                    </>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {(meterDailyUsageData?.items?.length === 0 ||
                    meterDailyUsageData?.items === undefined) && (
                    <div className="w-full h-[300px] flex items-center justify-center text-[25px]">
                        No Data{" "}
                    </div>
                )}
            </div>
            <div className="flex justify-between my-4">
                <div className=" font-bold px-4 flex items-center gap-3">
                    Total Items :{" "}
                    <div className="w-[100px]">
                        <TextInput
                            type={"number"}
                            value={filterParams?.pageSize}
                            onChange={debouncedOnFilter}
                        />
                    </div>
                </div>
                <div>
                    <Pagination
                        currentPage={meterDailyUsageData?.pageIndex}
                        totalPages={meterDailyUsageData?.totalPages}
                        onClickPage={handlePageClick}
                    />
                </div>
            </div>
        </div>
    );
}

export default DailyUsage;
